/**
 * Feature Flags Hook
 *
 * Provides feature flag functionality for conditional rendering.
 * Supports both individual flag checks and batch queries.
 *
 * @module hooks/useFeatureFlag
 *
 * @example
 * // Single flag check
 * const { isEnabled } = useFeatureFlag('advanced_analytics');
 *
 * // Multiple flags
 * const { flags } = useFeatureFlags(['b2b_module', 'crm_module']);
 *
 * // Conditional rendering
 * <Feature flag="crm_module">
 *   <CRMDashboard />
 * </Feature>
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.wasser-pro.com';

/**
 * Feature Flags Context
 */
const FeatureFlagsContext = createContext({
  features: {},
  isLoading: true,
  error: null,
  refresh: () => {},
});

/**
 * Feature Flags Provider Component
 */
export function FeatureFlagsProvider({ children }) {
  const [features, setFeatures] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Fetch feature flags from API
   */
  const fetchFeatures = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Get auth token from localStorage (admin or client)
      const token =
        localStorage.getItem('adminToken') ||
        localStorage.getItem('admin_token') ||
        localStorage.getItem('token') ||
        '';

      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_URL}/api/feature-flags/my`, {
        credentials: 'include',
        headers,
      });
      const data = await response.json();
      setFeatures(data.features || {});
    } catch (err) {
      console.error('Failed to fetch feature flags:', err);
      setError(err.message);
      // Set empty features on error (all flags disabled)
      setFeatures({});
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch on mount
  useEffect(() => {
    fetchFeatures();
  }, [fetchFeatures]);

  const value = useMemo(
    () => ({
      features,
      isLoading,
      error,
      refresh: fetchFeatures,
    }),
    [features, isLoading, error, fetchFeatures]
  );

  return (
    <FeatureFlagsContext.Provider value={value}>
      {children}
    </FeatureFlagsContext.Provider>
  );
}

/**
 * Hook to access feature flags context
 * @returns {Object} Feature flags context
 */
export function useFeatureFlagsContext() {
  return useContext(FeatureFlagsContext);
}

/**
 * Hook to check a single feature flag
 * @param {string} flagKey - The feature flag key
 * @returns {Object} { isEnabled, config, isBeta, isLoading }
 *
 * @example
 * const { isEnabled } = useFeatureFlag('advanced_analytics');
 * if (isEnabled) {
 *   return <AdvancedDashboard />;
 * }
 */
export function useFeatureFlag(flagKey) {
  const { features, isLoading } = useContext(FeatureFlagsContext);

  return useMemo(
    () => ({
      isEnabled: features[flagKey]?.enabled ?? false,
      config: features[flagKey]?.config ?? {},
      isBeta: features[flagKey]?.isBeta ?? false,
      isLoading,
    }),
    [features, flagKey, isLoading]
  );
}

/**
 * Hook to check multiple feature flags
 * @param {string[]} flagKeys - Array of feature flag keys
 * @returns {Object} { flags, isLoading, allEnabled, anyEnabled }
 *
 * @example
 * const { flags, anyEnabled } = useFeatureFlags(['b2b_module', 'crm_module']);
 * if (anyEnabled) {
 *   return <AdvancedFeatures flags={flags} />;
 * }
 */
export function useFeatureFlags(flagKeys) {
  const { features, isLoading } = useContext(FeatureFlagsContext);

  return useMemo(() => {
    const flags = {};
    let allEnabled = true;
    let anyEnabled = false;

    for (const key of flagKeys) {
      const isEnabled = features[key]?.enabled ?? false;
      flags[key] = isEnabled;

      if (!isEnabled) allEnabled = false;
      if (isEnabled) anyEnabled = true;
    }

    return { flags, isLoading, allEnabled, anyEnabled };
  }, [features, flagKeys, isLoading]);
}

/**
 * Higher-Order Component for feature flag gating
 * @param {React.Component} WrappedComponent - Component to wrap
 * @param {string} flagKey - Feature flag key
 * @param {React.Component} FallbackComponent - Optional fallback component
 * @returns {React.Component} Wrapped component
 *
 * @example
 * export default withFeatureFlag(CRMModule, 'crm_module', UpgradeBanner);
 */
export function withFeatureFlag(
  WrappedComponent,
  flagKey,
  FallbackComponent = null
) {
  return function FeatureFlaggedComponent(props) {
    const { isEnabled, isLoading } = useFeatureFlag(flagKey);

    if (isLoading) {
      return null;
    }

    if (!isEnabled) {
      return FallbackComponent ? <FallbackComponent {...props} /> : null;
    }

    return <WrappedComponent {...props} />;
  };
}

/**
 * Component for conditional rendering based on feature flag
 * @param {Object} props
 * @param {string} props.flag - Feature flag key
 * @param {React.ReactNode} props.children - Content to render if enabled
 * @param {React.ReactNode} props.fallback - Optional fallback content
 * @param {boolean} props.showLoading - Show children while loading (default: false)
 * @returns {React.ReactNode}
 *
 * @example
 * <Feature flag="advanced_analytics">
 *   <AnalyticsDashboard />
 * </Feature>
 *
 * <Feature flag="crm_module" fallback={<UpgradeBanner module="CRM" />}>
 *   <CRMModule />
 * </Feature>
 */
export function Feature({
  flag,
  children,
  fallback = null,
  showLoading = false,
}) {
  const { isEnabled, isLoading } = useFeatureFlag(flag);

  if (isLoading && !showLoading) {
    return null;
  }

  if (!isEnabled) {
    return fallback;
  }

  return children;
}

/**
 * Component for rendering content only when feature is disabled
 * Useful for showing upgrade prompts
 * @param {Object} props
 * @param {string} props.flag - Feature flag key
 * @param {React.ReactNode} props.children - Content to render if disabled
 * @returns {React.ReactNode}
 *
 * @example
 * <FeatureDisabled flag="advanced_analytics">
 *   <UpgradeBanner feature="Продвинутая аналитика" />
 * </FeatureDisabled>
 */
export function FeatureDisabled({ flag, children }) {
  const { isEnabled, isLoading } = useFeatureFlag(flag);

  if (isLoading) {
    return null;
  }

  if (isEnabled) {
    return null;
  }

  return children;
}

export default useFeatureFlag;
